import React from 'react';
import {
  getSigningAppointmentDisplayAddress,
  getSigningAppointmentDisplayDateTime,
  ISigningAppointmentResponse,
  signingAppointmentIsEditable,
} from '../../../../types/signingAppointment/ISigningAppointmentResponse';
import { CalendarIcon, HomeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { formatAppointmentDate } from '../../../../utilities/signingAppointments/formatAppointmentDate';
import { BodyTextTW, BoldBodyTextTW } from '../../../../tailwindComponents/bodyText/BodyTextTW';
import { propertyAddressToString } from '../../../../types/address/IPropertyAddressData';
import { ButtonTW } from '../../../../tailwindComponents/buttonTW/ButtonTW';
import { IPropertyAddressResponse } from '../../../../types/address/IPropertyAddressResponse';
import { eSigningMethod } from 'types/signingAppointment/eSigningMethod';
import { useSelectCurrentOrder } from '../../../../selectors/order/selectCurrentOrder';
import { useSelectAppointmentFromPathParam } from '../../../../utilities/order/scheduling/useSomeAppointmentFromParam';
import { useGetProgressionsQuery } from '../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { getCurrentSigSyncProgressionStatus } from '../utilities/getCurrentSigSyncProgressionStatus';
import { useParams } from 'react-router-dom';
import { DisplayPropertyType } from '../../../../ui-toolkit/orders/interfaces/ePropertyType';

export interface IAppointmentHeaderInformationProps {
  onEditClick: () => void;
}

export const AppointmentHeaderInformation: React.FC<IAppointmentHeaderInformationProps> = ({ onEditClick }) => {
  const { data: order } = useSelectCurrentOrder();
  const { appointmentFromPath } = useSelectAppointmentFromPathParam();
  const { orderId, appointmentId } = useParams<{ orderId: string; appointmentId: string }>();

  const { data: progressions } = useGetProgressionsQuery({
    id: appointmentId,
    orderId: orderId,
  });

  const progressionStatus = progressions ? getCurrentSigSyncProgressionStatus(progressions) : null;

  if (!appointmentFromPath) {
    return null;
  }

  const {
    appointmentDate: aptDate,
    appointmentTime: aptTime,
    abbreviatedTimeZone: aptAbrTz,
    correctionReasons: reasons,
  } = appointmentFromPath as ISigningAppointmentResponse;

  const propertyType = order.transaction.property_type;

  const correctionReasonsList =
    reasons
      ?.map((r) => r.label)
      .sort((l1, l2) => l1.localeCompare(l2))
      .join(', ') ?? '';

  const shouldDisplayRegularDateTimeInfo = Boolean(aptDate && aptTime);

  return (
    <>
      {!!reasons?.length && (
        <span className="block pb-2">
          <BoldBodyTextTW>Correction Reason(s): </BoldBodyTextTW>
          {correctionReasonsList}
        </span>
      )}
      <div className="flex justify-between">
        <div className="flex justify-start">
          <CalendarIcon className="mr-1 h-5 w-5" />
          {shouldDisplayRegularDateTimeInfo ? (
            <AppointmentDateTimeDisplay
              appointmentDate={aptDate as string}
              appointmentTime={aptTime as string}
              abbreviatedTimeZone={aptAbrTz}
            />
          ) : (
            <BoldBodyTextTW>{getSigningAppointmentDisplayDateTime(appointmentFromPath)}</BoldBodyTextTW>
          )}
        </div>
        {signingAppointmentIsEditable(appointmentFromPath, progressionStatus) && (
          <ButtonTW onClick={onEditClick} secondary={true} className="fa-pull-right w-min">
            Edit
          </ButtonTW>
        )}
      </div>

      <div className="flex w-3/4 justify-between">
        {appointmentFromPath.signingMethod !== eSigningMethod.eSign && (
          <div className="flex justify-start">
            <MapPinIcon className="mr-1 h-5 w-5" />
            <AppointmentLocationDisplay appointmentAddress={getSigningAppointmentDisplayAddress(appointmentFromPath)} />
          </div>
        )}

        {!!propertyType && (
          <div className="flex justify-start">
            <HomeIcon className="mr-1 h-5 w-5" />
            <BodyTextTW>{DisplayPropertyType[propertyType]}</BodyTextTW>
          </div>
        )}
      </div>
    </>
  );
};

const AppointmentDateTimeDisplay: React.FC<{
  appointmentDate?: string;
  appointmentTime?: string;
  abbreviatedTimeZone?: string;
}> = ({ appointmentDate, appointmentTime, abbreviatedTimeZone }) => {
  if (!(appointmentDate && appointmentTime)) {
    return <>Appointment Not Confirmed</>;
  }
  return (
    <BoldBodyTextTW>{formatAppointmentDate(appointmentDate, appointmentTime, abbreviatedTimeZone)}</BoldBodyTextTW>
  );
};

const AppointmentLocationDisplay: React.FC<{ appointmentAddress?: IPropertyAddressResponse | string }> = ({
  appointmentAddress,
}) => {
  if (!appointmentAddress) {
    return <>Location Pending Selection</>;
  }
  if (typeof appointmentAddress === 'string') {
    return <BodyTextTW>{appointmentAddress}</BodyTextTW>;
  }
  return <BodyTextTW>{propertyAddressToString(appointmentAddress)}</BodyTextTW>;
};
